import { toPosAmount } from '@/components/pos/posCatalog'

export const MAX_SPLIT_PAYMENTS = 5

/** @type {readonly string[]} */
export const REFERENCE_REQUIRED_METHOD_CODES = ['card', 'apple_pay', 'transfer']

/**
 * @typedef {object} PosPaymentRow
 * @property {string} key
 * @property {number | ''} payment_method_id
 * @property {string} amount
 * @property {string} reference
 */

/**
 * @param {import('@/types/api').PaymentMethod} method
 */
export function requiresPaymentReference(method) {
  return REFERENCE_REQUIRED_METHOD_CODES.includes(method.code)
}

/**
 * @param {import('@/types/api').PaymentMethod | undefined} method
 */
export function getReferenceLabel(method) {
  if (!method) return 'Reference'
  if (method.code === 'transfer') return 'Transfer reference'
  if (method.code === 'card' || method.code === 'apple_pay') return 'Auth / terminal ref'
  return 'Reference'
}

/**
 * @param {import('@/types/api').Sale | null | undefined} sale
 * @param {number} [tipAmount]
 */
export function computeAmountDue(sale, tipAmount = 0) {
  const currentTotal = toPosAmount(sale?.total)
  const currentTip = toPosAmount(sale?.tip_amount)
  return roundMoney(currentTotal - currentTip + tipAmount)
}

/**
 * @param {PosPaymentRow[]} rows
 */
export function sumPaymentRows(rows) {
  return roundMoney(
    rows.reduce((sum, row) => sum + toPosAmount(row.amount), 0),
  )
}

/**
 * @param {number} amountDue
 * @param {PosPaymentRow[]} rows
 */
export function computePaymentRemaining(amountDue, rows) {
  return roundMoney(amountDue - sumPaymentRows(rows))
}

/**
 * @param {PosPaymentRow[]} rows
 * @param {import('@/types/api').PaymentMethod[]} methods
 */
export function getCashPaymentAmount(rows, methods) {
  const cashMethod = methods.find((method) => method.code === 'cash')
  if (!cashMethod) return 0

  return roundMoney(
    rows
      .filter((row) => row.payment_method_id === cashMethod.id)
      .reduce((sum, row) => sum + toPosAmount(row.amount), 0),
  )
}

/**
 * @param {number} cashTendered
 * @param {PosPaymentRow[]} rows
 * @param {import('@/types/api').PaymentMethod[]} methods
 */
export function computeChangeDue(cashTendered, rows, methods) {
  const cashPaid = getCashPaymentAmount(rows, methods)
  if (cashPaid <= 0) return 0
  return roundMoney(Math.max(0, cashTendered - cashPaid))
}

/**
 * @param {import('@/types/api').PaymentMethod[]} methods
 * @param {number | ''} [preferredMethodId]
 */
export function createPaymentRow(methods, preferredMethodId) {
  const methodId = preferredMethodId
    ?? methods.find((method) => method.code === 'cash')?.id
    ?? methods[0]?.id
    ?? ''

  return {
    key: `pay-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    payment_method_id: methodId,
    amount: '',
    reference: '',
  }
}

/**
 * @param {PosPaymentRow[]} rows
 * @param {import('@/types/api').PaymentMethod[]} methods
 * @param {number} amountDue
 */
export function validatePaymentRows(rows, methods, amountDue) {
  const errors = []
  const usedMethodIds = new Set()

  if (rows.length === 0) {
    return ['Add at least one payment.']
  }

  rows.forEach((row, index) => {
    const method = methods.find((item) => item.id === row.payment_method_id)
    const amount = toPosAmount(row.amount)

    if (!method) {
      errors.push(`Payment ${index + 1}: select a method.`)
      return
    }

    if (usedMethodIds.has(method.id)) {
      errors.push(`Payment ${index + 1}: ${method.name} is already used.`)
    }
    usedMethodIds.add(method.id)

    if (amountDue <= 0) {
      if (amount < 0) {
        errors.push(`Payment ${index + 1}: enter a valid amount.`)
      }
    } else if (amount <= 0) {
      errors.push(`Payment ${index + 1}: enter an amount.`)
    }

    if (requiresPaymentReference(method) && !row.reference.trim()) {
      errors.push(`Payment ${index + 1}: ${getReferenceLabel(method)} is required.`)
    }
  })

  const remaining = computePaymentRemaining(amountDue, rows)
  if (Math.abs(remaining) > 0.01) {
    errors.push(`Payments must equal AED ${amountDue.toFixed(2)} (${remaining > 0 ? 'short' : 'over'} by AED ${Math.abs(remaining).toFixed(2)}).`)
  }

  return errors
}

/**
 * @param {import('@/types/api').Sale | null | undefined} sale
 */
export function resolveDefaultTipStylistId(sale) {
  const lines = (sale?.lines ?? [])
    .filter((line) => line.stylist_id && !line.parent_line_id)
    .sort((left, right) => toPosAmount(right.line_total) - toPosAmount(left.line_total))

  return lines[0]?.stylist_id ?? null
}

/**
 * @param {number} tipAmount
 * @param {'direct' | 'pool'} tipType
 * @param {number | null | undefined} tipStylistId
 */
export function validateTipSelection(tipAmount, tipType, tipStylistId) {
  if (tipAmount <= 0) return []

  if (tipType !== 'direct' && tipType !== 'pool') {
    return ['Select how the tip should be allocated.']
  }

  if (tipType === 'direct' && !tipStylistId) {
    return ['Select a stylist for a direct tip, or choose team pool.']
  }

  return []
}

/**
 * @param {PosPaymentRow[]} rows
 * @param {number} tipAmount
 * @param {number} cashTendered
 * @param {import('@/types/api').PaymentMethod[]} methods
 * @param {{ tipType?: 'direct' | 'pool', tipStylistId?: number | null }} [tipOptions]
 */
export function buildPayPayload(rows, tipAmount, cashTendered, methods, tipOptions = {}) {
  const payload = {
    tip_amount: tipAmount,
    payments: rows.map((row) => ({
      payment_method_id: Number(row.payment_method_id),
      amount: roundMoney(toPosAmount(row.amount)),
      reference: row.reference.trim() || undefined,
    })),
  }

  if (tipAmount > 0) {
    const tipType = tipOptions.tipType ?? 'direct'
    payload.tip_type = tipType
    if (tipType === 'direct' && tipOptions.tipStylistId) {
      payload.tip_stylist_id = tipOptions.tipStylistId
    }
  }

  const cashPaid = getCashPaymentAmount(rows, methods)
  if (cashPaid > 0 && cashTendered > 0) {
    payload.cash_tendered = roundMoney(cashTendered)
  }

  return payload
}

/**
 * @param {number} value
 */
export function roundMoney(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

import { saleMeetsGuestRequirement } from '@/components/pos/posCustomer'

/**
 * @param {import('@/types/api').Sale | null | undefined} sale
 */
export function canPaySaleLines(sale) {
  if (!sale || sale.status !== 'draft') return false
  return (sale.lines ?? []).some((line) => !line.parent_line_id)
}

/**
 * @param {import('@/types/api').Sale | null | undefined} sale
 */
export function canOpenPaymentModal(sale) {
  if (!canPaySaleLines(sale)) return false
  return saleMeetsGuestRequirement(sale)
}
